import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';
import cn from 'classnames';

import { getPricing, getLockInText } from '~/Config/Stripe';
import config from '~/Config';
import Card from '~/Components/Layout/Card/Card';
import StripePermittedView from '~/Components/Global/StripePermittedView';
import { guid, useMountEffect } from '~/util';
import Storage from '~/Providers/Storage';
import mParticle, { ScreenViewEvent, StartTrialEvent } from '~/Providers/mParticle';
import PaddleTotalWithTax from '~/Components/Global/Paddle/PaddleTotalWithTax';
import Spinner from '~/Components/Global/Loading/Spinner';
import history from '~/Providers/History';
import { setUser } from '~/Redux/Modules/User';
import { UserApi, paddleApi } from '~/Providers/Api';
import { error as showError } from '~/Redux/Modules/Notifications';

import './Subscription.scss';

const PaddleSubscription = () => {
  const [pricing, setPricing] = useState(getPricing());
  const [lockInText] = useState(getLockInText());
  const [eventId, setEventId] = useState(Storage.get('subscriptionEventId'));
  const [totals, setTotals] = useState({
    subtotal: pricing.yearly.price,
    tax: 0,
    total: pricing.yearly.price,
    coupon: null,
  });
  const dispatch = useDispatch();
  const user = useSelector(({ User }) => User.data);
  const isSubscribed = user.isSubscribed;
  let subscription = user.subscriptions?.sort((a, b) =>
    b.created_at.localeCompare(a.created_at)
  )?.[0];
  const [provisioning, setProvisioning] = useState(false);
  const pollingInterval = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pollingTime, setPollingTime] = useState(10000);

  const handlePaddleEvent = useCallback(
    event => {
      console.log('PaddleSubscription', 'event', event.name, event.data);

      // Update totals when we receive pricing events
      if (event.name && event.data?.recurring_totals) {
        const eventData = event.data;
        setTotals({
          subtotal: eventData.recurring_totals.subtotal,
          tax: eventData.recurring_totals.tax,
          total: eventData.recurring_totals.total,
          coupon: eventData.recurring_totals.discount
            ? {
                description: eventData.discount.code,
                amount: eventData.recurring_totals.discount,
              }
            : null,
        });
      }

      // Handle checkout completion
      if (event.name === 'checkout.completed') {
        // Show loading state and poll for provisioning status
        setProvisioning(true);
        setPollingTime(1000);
        mParticle.logEvent(new StartTrialEvent(pricing.yearly.commonIdentifier, ''));
      }
    },
    [setTotals, setProvisioning, dispatch, setPollingTime]
  );

  useEffect(() => {
    console.log('PaddleSubscription', 'useEffect', pollingInterval.current);
    pollingInterval.current = setInterval(async () => {
      console.log('PaddleSubscription', 'polling for subscription');
      const { data: apiUser } = await UserApi.fetch();
      console.log('PaddleSubscription', 'apiUser', apiUser);
      if (apiUser.isSubscribed) {
        console.log('PaddleSubscription', 'clearing polling interval');
        dispatch(setUser(apiUser));
        clearInterval(pollingInterval.current);
      }
    }, pollingTime);

    return () => {
      console.log('PaddleSubscription', 'useEffect release', pollingInterval.current);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [dispatch, pollingTime, pollingInterval]);

  useMountEffect(() => {
    if (!eventId) {
      const newId = guid();
      Storage.set('subscriptionEventId', newId);
      setEventId(newId);
    }
    mParticle.logEvent(new ScreenViewEvent('Manage Subscription'));
    async function initPaddle() {
      try {
        const paddleArgs = {
          token: config.paddleKey,
          checkout: {
            settings: {
              displayMode: 'inline',
              frameTarget: 'checkout-container',
              frameInitialHeight: '450',
              frameStyle:
                'width: 100%; min-width: 312px; background-color: transparent; border: none;',
              variant: 'one-page',
            },
          },
          eventCallback: handlePaddleEvent,
        };
        if (user.paddle_id) {
          paddleArgs.pwCustomer = {
            id: user.paddle_id,
          };
        }
        console.log('PaddleSubscription', 'paddleArgs', paddleArgs);
        if (!window.Paddle.Initialized) {
          window.Paddle.Environment.set(config.paddleEnvironment);
          window.Paddle.Initialize(paddleArgs);
        } else {
          window.Paddle.Update(paddleArgs);
        }

        setLoading(true);

        if (window.Rollbar) window.Rollbar.info('Paddle: Opening checkout');

        const { data: paddleData } = await paddleApi.subscribe({
          price: pricing.yearly.commonIdentifier,
        });

        window.Paddle.Checkout.open({
          transactionId: paddleData.transaction_id,
          customerAuthToken: paddleData.customer_auth_token,
          customer: {
            id: paddleData.customer_id,
          },
          customData: {
            eventId,
          },
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize Paddle checkout:', error);
        if (
          error?.message === 'You are already subscribed to this plan' ||
          error?.message?.indexOf('appendChild') !== -1
        ) {
          return;
        }
        dispatch(showError('Failed to initialize checkout. Please try again later.'));
      }
    }
    setTimeout(initPaddle, 1000);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  });

  useEffect(() => {
    console.log('PaddleSubscription', 'isSubscribed', isSubscribed, subscription);
    if (!isSubscribed) return;
    if (!subscription) return;

    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }

    if (totals.coupon) {
      history.push(
        `/add-accounts?coupon=${totals.coupon.description}&sas_tracking=${subscription.id}`
      );
    } else {
      history.push(`/add-accounts?sas_tracking=${subscription.id}`);
    }
  }, [isSubscribed, subscription, totals, pollingInterval]);

  const renderTrialText = () => {
    if (!user.isEligibleForTrial) {
      return <h2 className="center">Thank you for trying Artful Agenda!</h2>;
    }
    if (user.trial_ends_at == null) {
      return (
        <>
          <h2 className="center">Try Artful Agenda FREE for two weeks!</h2>
          <p className="center">Your card will not be charged until the end of your free trial.</p>
        </>
      );
    }
    return (
      <>
        <h2 className="center">Try Artful Agenda again for two weeks!</h2>
        <p className="center">Your card will not be charged until the end of your free trial.</p>
      </>
    );
  };

  return (
    <StripePermittedView
      disabledView={
        <Card noLogo>
          <div className="subscriptions">
            {(user.trial_ends_at != null && (
              <>
                <h2 className="center">Thank you for trying Artful Agenda!</h2>
                <hr />
              </>
            )) || (
              <>
                <h2 className="center">Try Artful Agenda FREE for two weeks!</h2>
                <hr />
              </>
            )}
            <p className="center margin-bottom">
              Please use the mobile app to set up your subscription.
            </p>
          </div>
        </Card>
      }
    >
      <Card noLogo className="card-wide">
        <div className="subscription">
          {renderTrialText()}
          <hr />

          <h3 className="center slight-margin-bottom">PRICING:</h3>

          {lockInText && <p>{lockInText}</p>}
          <div className="flex-row margin-bottom">
            <div className="price-wrapper">
              <div className={cn('plan year no-scale active')}>
                <span className="price">${pricing.yearly.displayPrice}</span>
                <span>{pricing.yearly.displayUnits}</span>
              </div>

              <h5 className="ad">({pricing.yearly.subtext})</h5>
            </div>
          </div>
          <div className="banner-wrapper">
            <div className="banner">
              <h6>Happiness Guaranteed!</h6>
              <span>Cancel at any time!</span>
              <span className="banner-triangle-left-container">
                <span className="banner-triangle-left" />
              </span>
              <span className="banner-triangle-right-container">
                <span className="banner-triangle-right" />
              </span>
            </div>
          </div>
          <h5 className="center margin-bottom">PAYMENT INFORMATION</h5>

          {/* Paddle Checkout Here */}
          <div className="checkout-wrapper">
            <div className="checkout-container" style={{ height: loading ? '450px' : null }} />

            <PaddleTotalWithTax {...totals} />

            {(provisioning || loading) && (
              <span className="spinner-wrapper">
                <Spinner />
              </span>
            )}
          </div>

          <input id="artfulUserId" type="hidden" name="userId" value={user.id} />
          <input id="artfulEventId" type="hidden" name="eventId" value={eventId} />
          <div className="disclosure">
            {`By signing up, you agree to Artful Agenda's`}{' '}
            <a href="http://artfulagenda.com/terms" className="terms">
              Terms of Service
            </a>
          </div>
        </div>
      </Card>
    </StripePermittedView>
  );
};

PaddleSubscription.propTypes = {
  isFetching: propTypes.bool,
  name: propTypes.string,
  coupon: propTypes.string,
  subscribe: propTypes.func,
  plan: propTypes.string,
  user: propTypes.object,
};

PaddleSubscription.defaultProps = {};

export default PaddleSubscription;
